class ScrollVideoSection extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('.scroll-video__media');
    if (!this.video) return;

    this.rafPending = false;
    this.onScroll = this.onScroll.bind(this);
    this.onMetadata = this.onMetadata.bind(this);

    // The video must not autoplay — we manually control currentTime via scroll.
    this.video.pause();

    if (this.video.readyState >= 1 /* HAVE_METADATA */) {
      this.onMetadata();
    } else {
      this.video.addEventListener('loadedmetadata', this.onMetadata, { once: true });
    }
  }

  onMetadata() {
    this.duration = this.video.duration || 0;
    if (this.duration <= 0) return;

    // iOS Safari + some Android browsers do NOT decode frames when you change
    // currentTime on a never-played <video>. The property updates but the
    // visible frame stays frozen. Workaround: kick the decoder by playing for
    // one tick, then pausing. The video is `muted + playsinline` so this is
    // allowed without user interaction.
    const warmUp = this.video.play();
    if (warmUp && typeof warmUp.then === 'function') {
      warmUp.then(() => this.video.pause()).catch(() => {});
    } else {
      // Older browsers return undefined; pause on next tick.
      setTimeout(() => this.video.pause(), 0);
    }

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.onScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }

  onScroll() {
    if (this.rafPending) return;
    this.rafPending = true;
    requestAnimationFrame(() => {
      this.rafPending = false;
      this.update();
    });
  }

  update() {
    if (!this.video || !this.duration) return;

    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight;

    // Skip when section is entirely out of view.
    if (rect.bottom < 0 || rect.top > vh) return;

    // Progress starts the moment the section's top edge enters the viewport
    // from below (rect.top == vh). By default totalScroll = rect.height, but a
    // data-scrub-length-svh attribute decouples the scrub distance from the
    // section's physical height (useful when CSS shrinks the section but we
    // still want a slow scrub).
    const customSvh = parseFloat(this.dataset.scrubLengthSvh);
    const totalScroll = customSvh > 0 ? customSvh * vh / 100 : rect.height;
    const scrolled = vh - rect.top;
    let progress = scrolled / totalScroll;
    progress = Math.max(0, Math.min(1, progress));

    const targetTime = progress * this.duration;
    // Skip tiny deltas to avoid hammering the decoder.
    if (Math.abs(targetTime - this.video.currentTime) < 0.02) return;
    this.video.currentTime = targetTime;
  }
}

if (!customElements.get('scroll-video-section')) {
  customElements.define('scroll-video-section', ScrollVideoSection);
}
